import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import MascotBubble, { MascotAvatar } from '@/components/MascotBubble';

export default function AddChild() {
    const { refreshChildren, pickActiveChild } = useAuth();
    const navigate = useNavigate();
    const [languages, setLanguages] = useState([]);
    const [form, setForm] = useState({
        display_name: '', age: 7, preferred_language_ids: [], avatar_key: 'avatar-purple',
        preferred_difficulty: '', // auto-derived from age unless parent overrides
        consent_checkbox_ticked: false,
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    const autoDifficulty = form.age <= 7 ? 'easy' : form.age <= 12 ? 'medium' : 'hard';
    const effectiveDifficulty = form.preferred_difficulty || autoDifficulty;

    useEffect(() => {
        api.get('/languages?status=live').then(({ data }) => setLanguages(data.languages.filter((l) => l.status === 'live')));
    }, []);

    const toggleLang = (id) => {
        setForm((f) => ({
            ...f,
            preferred_language_ids: f.preferred_language_ids.includes(id)
                ? f.preferred_language_ids.filter((x) => x !== id)
                : [...f.preferred_language_ids, id],
        }));
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.consent_checkbox_ticked) { setErr('Please tick the consent box.'); return; }
        setBusy(true); setErr(null);
        try {
            const payload = { ...form, preferred_difficulty: form.preferred_difficulty || autoDifficulty };
            const { data } = await api.post('/me/children', payload);
            await refreshChildren();
            pickActiveChild(data.child.id);
            navigate('/dashboard');
        } catch (e) { setErr(e?.response?.data?.detail || 'Something went wrong'); }
        finally { setBusy(false); }
    };

    return (
        <main className="max-w-3xl mx-auto px-6 py-14" data-testid="page-add-child">
            <div className="flex items-end gap-4">
                <MascotAvatar variant="gogo" />
                <MascotBubble tone="peach">
                    "Lovely! Tell us a little about your grandchild and we'll match the puzzles to their level."
                </MascotBubble>
            </div>
            <h1 className="mt-8 font-display text-3xl md:text-4xl font-semibold text-lq-ink-black">Add a child profile</h1>
            <p className="mt-2 text-lq-ink-500">We ask only for their first name or nickname, and their age. No surname. No date of birth. No address.</p>
            <form onSubmit={submit} className="mt-8 space-y-5" data-testid="add-child-form">
                <label className="block">
                    <span className="text-sm font-medium text-lq-ink-900">First name or nickname</span>
                    <input required maxLength={40} value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} className="mt-1 w-full rounded-lg border border-lq-ink-300 px-3 py-2" data-testid="child-name" />
                </label>
                <label className="block">
                    <span className="text-sm font-medium text-lq-ink-900">Age: {form.age}</span>
                    <input type="range" min={4} max={17} value={form.age} onChange={(e) => setForm({ ...form, age: parseInt(e.target.value) })} className="mt-2 w-full" data-testid="child-age" />
                    <div className="text-xs text-lq-ink-500 mt-1">
                        Age tier: <strong className="text-lq-ink-900">{form.age <= 7 ? 'Seedlings (4–7)' : form.age <= 11 ? 'Growers (8–11)' : form.age <= 15 ? 'Explorers (12–15)' : 'Teens (16+)'}</strong>
                    </div>
                </label>
                <div>
                    <span className="text-sm font-medium text-lq-ink-900">Languages to start with</span>
                    <div className="mt-2 flex flex-wrap gap-2">
                        {languages.map((l) => {
                            const on = form.preferred_language_ids.includes(l.id);
                            return (
                                <button
                                    type="button" key={l.id} onClick={() => toggleLang(l.id)}
                                    className={`px-4 py-2 rounded-full text-sm border-2 transition-colors ${
                                        on ? 'bg-lq-purple-500 text-white border-lq-purple-500' : 'bg-white border-lq-ink-100 text-lq-ink-900 hover:border-lq-purple-300'
                                    }`}
                                    data-testid={`lang-toggle-${l.slug}`}
                                >{l.primary_flag_emoji} {l.name}</button>
                            );
                        })}
                    </div>
                </div>
                <div>
                    <span className="text-sm font-medium text-lq-ink-900">Difficulty</span>
                    <p className="text-xs text-lq-ink-500 mt-1">Parents only. Easy for ages 4–7, Medium for 8–12, Hard for 13+. We pick a default from age — change any time from the Dashboard.</p>
                    <div className="mt-2 flex flex-wrap gap-2" role="radiogroup" aria-label="Difficulty">
                        {[
                            { v: 'easy', label: 'Easy' },
                            { v: 'medium', label: 'Medium' },
                            { v: 'hard', label: 'Hard' },
                        ].map((d) => {
                            const on = effectiveDifficulty === d.v;
                            return (
                                <button
                                    type="button" key={d.v}
                                    onClick={() => setForm({ ...form, preferred_difficulty: d.v })}
                                    className={`px-4 py-2 rounded-full text-sm border-2 transition-colors ${
                                        on ? 'bg-lq-peach-500 text-white border-lq-peach-500' : 'bg-white border-lq-ink-100 text-lq-ink-900 hover:border-lq-peach-300'
                                    }`}
                                    data-testid={`difficulty-${d.v}`}
                                    aria-checked={on} role="radio"
                                >{d.label}{form.preferred_difficulty === '' && d.v === autoDifficulty ? ' (auto)' : ''}</button>
                            );
                        })}
                    </div>
                </div>
                <div className="rounded-xl bg-lq-purple-50 border border-lq-purple-100 p-4">
                    <label className="flex gap-2 items-start">
                        <input type="checkbox" checked={form.consent_checkbox_ticked} onChange={(e) => setForm({ ...form, consent_checkbox_ticked: e.target.checked })} className="mt-1" data-testid="consent-checkbox" />
                        <span className="text-sm text-lq-ink-900">
                            I am the parent or legal guardian of <strong>{form.display_name || 'this child'}</strong> (age {form.age}) and consent to their use of Linguistic Quest. I understand their progress data will be processed as described in the Privacy Policy.
                        </span>
                    </label>
                    <p className="mt-2 text-xs text-lq-ink-500">A timestamped consent record is stored with your IP for compliance with UK AADC, GDPR, and COPPA. You can revoke consent and delete this profile any time.</p>
                </div>
                {err && <div className="text-sm text-lq-error" role="alert">{err}</div>}
                <button disabled={busy} className="bg-lq-purple-500 text-white font-semibold py-3 px-6 rounded-full hover:bg-lq-purple-700 disabled:opacity-60" data-testid="add-child-submit">
                    {busy ? 'Creating…' : 'Create profile'}
                </button>
            </form>
        </main>
    );
}
